import type {
  ResolutionAt,
  ResolutionSnapshot,
  ResolutionThresholdDecayPolicy,
} from '../conviction/interfaces.js';
import { CONVICTION_SCALE_PPB, fixedPowPpb } from './conviction.js';
import {
  effectiveResolutionThresholdPpbFromPolicy,
  resolutionThresholdDropCount,
} from './resolution-threshold.js';

const I64_MAX = (1n << 63n) - 1n;
const I64_MIN = -(1n << 63n);

interface ExactProjection {
  numerators: readonly bigint[];
  totalNumerator: bigint;
}

export function projectFirstTouchResolution(
  snapshot: ResolutionSnapshot,
  policy: ResolutionThresholdDecayPolicy,
): ResolutionAt | null {
  if (!isValidSegment(snapshot, policy)) return null;

  const checkpoints = checkpointTimestamps(snapshot, policy);
  for (let index = 0; index < checkpoints.length; index += 1) {
    const checkpoint = checkpoints[index];
    if (checkpoint === undefined) return null;

    const winner = winnerAt(snapshot, policy, checkpoint);
    if (winner !== null) return resolutionAt(checkpoint, winner);

    const next = checkpoints[index + 1];
    if (next === undefined) continue;
    const firstInside = firstTouchInStaticRange(snapshot, policy, checkpoint + 1n, next - 1n);
    if (firstInside !== null) return firstInside;
  }
  return null;
}

function checkpointTimestamps(
  snapshot: ResolutionSnapshot,
  policy: ResolutionThresholdDecayPolicy,
): bigint[] {
  const values = new Set<bigint>([snapshot.updateTsSecs]);
  const origin = policy.tradingStartedTs + policy.decayDelaySecs;
  const drops = resolutionThresholdDropCount(
    policy.initialThresholdPpb,
    policy.thresholdFloorPpb,
    policy.dropPpb,
  );
  for (let index = 1n; index <= drops; index += 1n) {
    const timestamp = origin + index * policy.dropIntervalSecs;
    if (timestamp > snapshot.updateTsSecs && timestamp <= I64_MAX) values.add(timestamp);
  }
  values.add(snapshot.updateTsSecs + retentionSearchElapsed(snapshot));
  return [...values].sort(compareBigint);
}

function retentionSearchElapsed(snapshot: ResolutionSnapshot): bigint {
  const maximumElapsed = I64_MAX - snapshot.updateTsSecs;
  if (maximumElapsed === 0n || fixedPowPpb(snapshot.retentionPpb, maximumElapsed) > 0n) {
    return maximumElapsed;
  }

  let low = 0n;
  let high = maximumElapsed;
  while (low + 1n < high) {
    const middle = low + (high - low) / 2n;
    if (fixedPowPpb(snapshot.retentionPpb, middle) === 0n) high = middle;
    else low = middle;
  }
  return high;
}

function firstTouchInStaticRange(
  snapshot: ResolutionSnapshot,
  policy: ResolutionThresholdDecayPolicy,
  low: bigint,
  high: bigint,
): ResolutionAt | null {
  if (low > high) return null;
  const threshold = effectiveResolutionThresholdPpbFromPolicy(policy, low);
  const anchorTotal = sum(snapshot.convictionPpb);
  const targetTotal = sum(snapshot.targetPpb);
  let firstTimestamp: bigint | null = null;

  for (let outcomeIndex = 0; outcomeIndex < snapshot.convictionPpb.length; outcomeIndex += 1) {
    if (!shareNonDecreasing(snapshot, outcomeIndex, anchorTotal, targetTotal)) continue;
    if (!outcomeMeets(snapshot, outcomeIndex, high, threshold)) continue;
    const timestamp = firstTrue(low, high, (candidate) =>
      outcomeMeets(snapshot, outcomeIndex, candidate, threshold),
    );
    if (firstTimestamp === null || timestamp < firstTimestamp) firstTimestamp = timestamp;
  }

  if (firstTimestamp === null) return null;
  const winner = winnerAt(snapshot, policy, firstTimestamp);
  return winner === null ? null : resolutionAt(firstTimestamp, winner);
}

function firstTrue(low: bigint, high: bigint, predicate: (timestamp: bigint) => boolean): bigint {
  if (predicate(low)) return low;
  let left = low;
  let right = high;
  while (left + 1n < right) {
    const middle = left + (right - left) / 2n;
    if (predicate(middle)) right = middle;
    else left = middle;
  }
  return right;
}

function winnerAt(
  snapshot: ResolutionSnapshot,
  policy: ResolutionThresholdDecayPolicy,
  timestamp: bigint,
): number | null {
  const projection = exactProjection(snapshot, timestamp);
  if (projection.totalNumerator === 0n) return null;

  let winner = 0;
  for (let index = 1; index < projection.numerators.length; index += 1) {
    if (requiredAt(projection.numerators, index) > requiredAt(projection.numerators, winner)) {
      winner = index;
    }
  }
  const threshold = effectiveResolutionThresholdPpbFromPolicy(policy, timestamp);
  return outcomeMeetsProjection(projection, winner, threshold) ? winner : null;
}

function outcomeMeets(
  snapshot: ResolutionSnapshot,
  outcomeIndex: number,
  timestamp: bigint,
  threshold: bigint,
): boolean {
  return outcomeMeetsProjection(exactProjection(snapshot, timestamp), outcomeIndex, threshold);
}

function outcomeMeetsProjection(
  projection: ExactProjection,
  outcomeIndex: number,
  threshold: bigint,
): boolean {
  if (projection.totalNumerator === 0n) return false;
  return (
    CONVICTION_SCALE_PPB * requiredAt(projection.numerators, outcomeIndex) >=
    threshold * projection.totalNumerator
  );
}

function exactProjection(snapshot: ResolutionSnapshot, timestamp: bigint): ExactProjection {
  const elapsed = timestamp - snapshot.updateTsSecs;
  const retention = fixedPowPpb(snapshot.retentionPpb, elapsed);
  const numerators = snapshot.convictionPpb.map((anchor, index) => {
    const target = requiredAt(snapshot.targetPpb, index);
    const moving = anchor >= target ? anchor - target : target - anchor;
    const targetScaled = target * CONVICTION_SCALE_PPB;
    return anchor >= target ? targetScaled + moving * retention : targetScaled - moving * retention;
  });
  return { numerators, totalNumerator: sum(numerators) };
}

function shareNonDecreasing(
  snapshot: ResolutionSnapshot,
  outcomeIndex: number,
  anchorTotal: bigint,
  targetTotal: bigint,
): boolean {
  return (
    requiredAt(snapshot.targetPpb, outcomeIndex) * anchorTotal >=
    requiredAt(snapshot.convictionPpb, outcomeIndex) * targetTotal
  );
}

function isValidSegment(
  snapshot: ResolutionSnapshot,
  policy: ResolutionThresholdDecayPolicy,
): boolean {
  const count = snapshot.convictionPpb.length;
  if (
    count < 2 ||
    count > 10 ||
    snapshot.targetPpb.length !== count ||
    snapshot.retentionPpb <= 0n ||
    snapshot.retentionPpb >= CONVICTION_SCALE_PPB ||
    snapshot.updateTsSecs < policy.tradingStartedTs ||
    !isI64(snapshot.updateTsSecs) ||
    !isI64(policy.tradingStartedTs) ||
    [...snapshot.convictionPpb, ...snapshot.targetPpb].some(
      (value) => value < 0n || value > CONVICTION_SCALE_PPB,
    ) ||
    sum(snapshot.targetPpb) === 0n
  ) {
    return false;
  }
  try {
    effectiveResolutionThresholdPpbFromPolicy(policy, snapshot.updateTsSecs);
    return true;
  } catch {
    return false;
  }
}

function resolutionAt(resolutionAtSecs: bigint, outcomeIndex: number): ResolutionAt {
  return { outcomeIndex, resolutionAtSecs };
}

function requiredAt(values: readonly bigint[], index: number): bigint {
  const value = values[index];
  if (value === undefined) throw new RangeError(`missing outcome ${index}`);
  return value;
}

function sum(values: readonly bigint[]): bigint {
  return values.reduce((total, value) => total + value, 0n);
}

function compareBigint(left: bigint, right: bigint): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function isI64(value: bigint): boolean {
  return value >= I64_MIN && value <= I64_MAX;
}
